import { Link } from "react-router-dom";
import {
  Shield,
  Globe2,
  Zap,
  Wallet,
  ArrowRight,
  CheckCircle2,
  Lock,
  EyeOff,
  TrendingUp,
  Clock,
} from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import { buildWebSiteJsonLd, buildBreadcrumbListJsonLd } from "../../seo/site";

const SEO_TITLE = "Nos valeurs — Raguia | IA documentaire souveraine et simple";
const SEO_DESCRIPTION =
  "Découvrez les valeurs de Raguia : sécurité maximale, souveraineté française, simplicité clé en main et pricing prévisible. L'assistant IA B2B qui respecte vos données et votre budget.";

function SecuriteSection() {
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="size-14 rounded-xl bg-[#A43032]/10 flex items-center justify-center shrink-0">
            <Shield className="size-7 text-[#A43032]" />
          </div>
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101]">
              Sécurité maximale
            </h2>
            <p className="text-stone-500 text-sm md:text-base mt-0.5">
              Vos données sont votre actif le plus précieux
            </p>
          </div>
        </div>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Chiffrement <span className="font-mono font-bold">TLS 1.3</span>{" "}
              en transit et <span className="font-mono font-bold">AES-256</span>{" "}
              au repos
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Isolation stricte par client : architecture multi-tenant
              cloisonnée
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Zéro entraînement sur vos documents — vos fichiers ne servent
              jamais à améliorer l&rsquo;IA
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Hébergement exclusif chez OVH en France, sans sous-traitance
              offshore
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Suppression garantie des données sous 30 jours après résiliation
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}

function SouveraineteSection() {
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200 bg-stone-50/50">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="size-14 rounded-xl bg-[#A43032]/10 flex items-center justify-center shrink-0">
            <Globe2 className="size-7 text-[#A43032]" />
          </div>
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101]">
              Confidentialité & Souveraineté française
            </h2>
            <p className="text-stone-500 text-sm md:text-base mt-0.5">
              Vos documents ne quittent jamais la juridiction européenne
            </p>
          </div>
        </div>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Serveurs 100% français (OVH), aucune donnée aux États-Unis
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Modèles IA européens (
              <span className="font-mono font-bold">Mistral AI</span>,
              entreprise française) — pas de dépendance américaine
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Conformité <span className="font-mono font-bold">RGPD</span>{" "}
              intégrale : DPA disponible, traitements documentés
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Pas de CLOUD Act : vos données sont hors de portée des lois
              extraterritoriales US
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Aucune revente, aucun partage, aucun croisement de données entre
              clients
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}

function SimpliciteSection() {
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="size-14 rounded-xl bg-[#A43032]/10 flex items-center justify-center shrink-0">
            <Zap className="size-7 text-[#A43032]" />
          </div>
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101]">
              Clé en main & Simplicité radicale
            </h2>
            <p className="text-stone-500 text-sm md:text-base mt-0.5">
              Zéro code, zéro configuration complexe
            </p>
          </div>
        </div>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Upload de documents aussi simple qu&rsquo;un Drive :
              glissez-déposez vos PDF, Word, Excel
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              L&rsquo;IA est prête à répondre instantanément — pas de création
              d&rsquo;assistants ni de prompts
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Portail client intuitif : vos équipes l&rsquo;utilisent sans
              formation
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Chatbot accessible par lien sécurisé, intégrable en{" "}
              <span className="font-mono font-bold">5 minutes</span>
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Pas de maintenance : mises à jour, sauvegardes et supervision
              incluses
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200 bg-stone-50/50">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="size-14 rounded-xl bg-[#A43032]/10 flex items-center justify-center shrink-0">
            <Wallet className="size-7 text-[#A43032]" />
          </div>
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101]">
              Pricing prévisible & transparent
            </h2>
            <p className="text-stone-500 text-sm md:text-base mt-0.5">
              Pas de surprise sur votre facture, jamais
            </p>
          </div>
        </div>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Forfait fixe mensuel à partir de{" "}
              <span className="font-mono font-bold">90 €/mois</span> — pas de
              coût par utilisateur
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Pas de limite sur le volume de documents ou le nombre de requêtes
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Setup unique et transparent :{" "}
              <span className="font-mono">250 €</span> à{" "}
              <span className="font-mono">1 000 €</span> selon la formule
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Aucun coût caché : pas de frais d&rsquo;API, pas de surcoût modèle
              IA
            </span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle2 className="size-5 text-[#A43032] shrink-0 mt-0.5" />
            <span className="text-stone-700 leading-relaxed">
              Engagement sans risque : résiliation à tout moment, sans frais
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}

export default function ValeursPage() {
  return (
    <main className="min-h-screen bg-stone-50 font-body text-[#010101] selection:bg-[#A43032] selection:text-white">
      <SeoHead
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        jsonLd={[
          buildWebSiteJsonLd(),
          buildBreadcrumbListJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Nos valeurs", path: "/valeurs" },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative px-4 pt-12 pb-6 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
        <div className="absolute top-20 left-10 size-72 bg-[#A43032]/5 rounded-full blur-3xl" />
        <div className="absolute top-40 right-10 size-96 bg-[#A43032]/8 rounded-full blur-3xl" />

        <div className="relative flex flex-col-reverse md:flex-row items-center gap-6 md:gap-12 lg:gap-16">
          {/* Texte */}
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#A43032]/30 bg-[#A43032]/10 px-4 py-2 text-sm font-semibold text-[#A43032] mb-6">
              <Shield className="size-4" /> Nos engagements
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-[#010101] leading-[1.1] mb-6">
              Ce en quoi nous croyons
            </h1>
            <p className="text-lg md:text-xl text-stone-600 max-w-2xl font-medium mb-6 mx-auto md:mx-0">
              Quatre piliers guident chaque décision chez Raguia. Parce
              qu&rsquo;une IA documentaire pour les PME françaises doit être
              sécurisée, souveraine, simple et prévisible.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#A43032] px-8 py-4 text-base font-bold text-white shadow-xl hover:scale-105 hover:bg-[#8a2729] transition-all"
            >
              Demander une démo <ArrowRight className="size-5" />
            </Link>
          </div>

          {/* Photo */}
          <div className="shrink-0 flex flex-col items-center gap-3">
            <div className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-80 lg:h-80">
              <div className="relative size-full rounded-3xl border-2 border-stone-200 bg-stone-100 shadow-2xl overflow-hidden">
                <img
                  src="/Valentin_Fiess.webp"
                  alt="Valentin Fiess — fondateur de Raguia"
                  className="size-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="text-center">
              <p className="font-heading text-base md:text-lg font-bold text-[#010101]">
                Valentin Fiess
              </p>
              <p className="text-xs md:text-sm text-stone-500">
                Fondateur, Dirigeant & Développeur IA/Data
              </p>
            </div>
          </div>
        </div>
      </section>

      <SecuriteSection />
      <SouveraineteSection />
      <SimpliciteSection />
      <PricingSection />

      {/* ENGAGEMENTS CHIFFRÉS */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101] mb-10 text-center">
          Nos engagements en chiffres
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border-2 border-stone-200 p-5 text-center shadow-sm hover:border-[#A43032]/30 hover:shadow-md transition-all">
            <Lock className="size-6 text-[#A43032] mx-auto mb-3" />
            <p className="font-heading font-mono text-2xl md:text-3xl font-black text-[#010101]">
              100%
            </p>
            <p className="text-xs text-stone-500 mt-1">Hébergement France</p>
          </div>
          <div className="bg-white rounded-2xl border-2 border-stone-200 p-5 text-center shadow-sm hover:border-[#A43032]/30 hover:shadow-md transition-all">
            <EyeOff className="size-6 text-[#A43032] mx-auto mb-3" />
            <p className="font-heading font-mono text-2xl md:text-3xl font-black text-[#010101]">
              0
            </p>
            <p className="text-xs text-stone-500 mt-1">
              Entraînement sur vos données
            </p>
          </div>
          <div className="bg-white rounded-2xl border-2 border-stone-200 p-5 text-center shadow-sm hover:border-[#A43032]/30 hover:shadow-md transition-all">
            <Clock className="size-6 text-[#A43032] mx-auto mb-3" />
            <p className="font-heading font-mono text-2xl md:text-3xl font-black text-[#010101]">
              5 min
            </p>
            <p className="text-xs text-stone-500 mt-1">
              Pour intégrer le chatbot
            </p>
          </div>
          <div className="bg-white rounded-2xl border-2 border-stone-200 p-5 text-center shadow-sm hover:border-[#A43032]/30 hover:shadow-md transition-all">
            <TrendingUp className="size-6 text-[#A43032] mx-auto mb-3" />
            <p className="font-heading font-mono text-2xl md:text-3xl font-black text-[#A43032]">
              90 €
            </p>
            <p className="text-xs text-stone-500 mt-1">Par mois, prix fixe</p>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-[#A43032] to-[#8a2729] rounded-3xl p-10 text-white text-center shadow-2xl">
          <h2 className="font-heading text-3xl font-semibold mb-4">
            Des valeurs fortes, une IA fiable
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Testez Raguia gratuitement sur vos propres documents. Aucun
            engagement, hébergement en France.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-[#A43032] transition hover:scale-105"
          >
            Demander une démo <ArrowRight className="size-5" />
          </Link>
        </div>
      </section>
    </main>
  );
}
